/**
 * createSubdomainPersistence — canonical localStorage-backed remembered-subdomain
 * store for the cross-domain marketing login (MKT-LOGIN-XDOMAIN-001, 2026-06-29).
 *
 * Returns the `{ getStoredSubdomain, clearStoredSubdomain, setStoredSubdomain }`
 * shape `useSubdomainDetection` consumes. Promoted so the 8 marketing apps don't
 * each hand-roll the same try/catch'd localStorage wrapper (only pos-web had one,
 * in `core/utils/credentialPersistence`).
 *
 * The ONE expected failure is the browser REFUSING Web Storage (HTML Living
 * Standard, "Web storage"): the `localStorage` getter throws a `SecurityError`
 * DOMException when storage is blocked, and `setItem` a `QuotaExceededError`
 * when it cannot store (Safari's legacy private mode has a quota of zero). A
 * refused read answers "no remembered subdomain" (the person types it, exactly
 * as on a first visit); a refused clear or write changes nothing. Anything else
 * is a defect and surfaces — the old catches swallowed everything
 * (SWALLOWED-CATCH-BURNDOWN-001 U3).
 *
 * @module @puffin-core/login-shell/utils/createSubdomainPersistence
 */

/**
 * @param {object} config
 * @param {string} config.storageKey - The localStorage key (app-namespaced,
 *   e.g. `palm-auth_subdomain`).
 * @param {Storage} [config.storage] - Storage implementation. Defaults to
 *   `window.localStorage` when available (null in SSR/headless).
 * @returns {{getStoredSubdomain: () => (string|null), clearStoredSubdomain: () => void, setStoredSubdomain: (value: string) => void}}
 */
/** The two DOMException names a browser raises when it refuses Web Storage. */
const STORAGE_REFUSAL_NAMES = new Set(['SecurityError', 'QuotaExceededError']);

/**
 * Run `operation` against the store; a browser refusal answers `refused`, anything else rethrows.
 *
 * @template T
 * @param {() => T} operation
 * @param {T} refused - the answer when the browser refuses storage
 * @returns {T}
 */
function withStorage(operation, refused) {
  try {
    return operation();
  } catch (error) {
    if (!(error instanceof DOMException && STORAGE_REFUSAL_NAMES.has(error.name))) throw error;
    return refused;
  }
}

export function createSubdomainPersistence({ storageKey, storage } = {}) {
  if (!storageKey || typeof storageKey !== 'string') {
    throw new Error('storageKey is required for createSubdomainPersistence');
  }

  const resolveStorage = () => {
    if (storage) return storage;
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    return null;
  };

  return {
    getStoredSubdomain() {
      return withStorage(() => {
        const store = resolveStorage();
        return store ? store.getItem(storageKey) : null;
      }, null);
    },
    clearStoredSubdomain() {
      withStorage(() => {
        const store = resolveStorage();
        if (store) store.removeItem(storageKey);
      }, undefined);
    },
    setStoredSubdomain(value) {
      withStorage(() => {
        const store = resolveStorage();
        if (store && value) store.setItem(storageKey, value);
      }, undefined);
    },
  };
}

export default createSubdomainPersistence;
